import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { STATE_CODES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Building2, MapPin, ArrowRight, Shield, LogOut, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const BUSINESS_SECTORS = [
  "Solar & Renewable Energy",
  "Education & Training",
  "Gems & Jewellery",
  "Professional Services",
  "General Trading",
  "Construction",
  "Marketing & Advertising",
  "Restaurant & Food Services",
  "Other",
];

const GST_STATUSES = [
  { value: "registered_regular", label: "Registered — Regular" },
  { value: "registered_composition", label: "Registered — Composition Scheme" },
  { value: "unregistered", label: "Unregistered / Exempt" },
];

const COMPOSITION_CATEGORIES = [
  { value: "manufacturers_traders", label: "Manufacturers / Traders — 1% (0.5% CGST + 0.5% SGST)" },
  { value: "restaurants", label: "Restaurants (No Alcohol) — 5% (2.5% CGST + 2.5% SGST)" },
  { value: "service_providers", label: "Service Providers — 6% (3% CGST + 3% SGST)" },
];

// E.164 international phone format — leading `+`, country code, then 7-14 digits.
// Reference: ITU-T E.164 standard (max 15 digits worldwide, min 8).
const E164_REGEX = /^\+[1-9]\d{6,14}$/;

/** Strip formatting on-the-fly as the user types — preserves one leading `+` and digits only. */
function maskE164(raw) {
  if (!raw) return "";
  const s = String(raw);
  const hasPlus = s.trim().startsWith("+");
  const digits = s.replace(/\D+/g, "");
  return hasPlus || digits.length > 0 ? `+${digits}` : "";
}

export default function OnboardingPage() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    business_name: "", business_address: "", city: "", state_code: "", pin: "",
    gst_status: "unregistered", gstin: "",
    business_sector: "", composition_category: "",
    phone_number: "",
  });

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Logged out");
      navigate("/");
    } catch (err) {
      toast.error("Logout failed");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.business_name || !form.state_code) {
      toast.error("Business Name and State are required"); return;
    }
    if (!form.phone_number || !E164_REGEX.test(form.phone_number)) {
      toast.error("Enter a valid WhatsApp number in E.164 format, e.g. +919812345678");
      return;
    }
    if (form.gst_status === "registered_regular" && (!form.gstin || form.gstin.length !== 15)) {
      toast.error("Valid 15-character GSTIN required"); return;
    }
    if (form.gst_status === "registered_composition" && (!form.gstin || form.gstin.length !== 15)) {
      toast.error("GSTIN required for Composition Scheme"); return;
    }
    if (form.gst_status === "registered_composition" && !form.composition_category) {
      toast.error("Select your Composition Category"); return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        is_gst_registered: form.gst_status !== "unregistered",
      };
      const response = await api.put("/auth/onboarding", payload);
      setUser(response.data);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const selectedState = STATE_CODES.find((s) => s.code === form.state_code);

  return (
    <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-6" data-testid="onboarding-page">
      <div className="bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,0,0,0.08)] border border-[#e2e8f0] p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <img src="/bizflowa-logo.png" alt="BizFlowa" className="h-10 w-10 rounded-xl object-contain" />
            <span className="font-outfit text-xl font-bold tracking-tight text-[#0f172a]">BizFlowa</span>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="onboarding-logout-btn"
            className="flex items-center gap-1.5 text-xs text-[#64748b] hover:text-[#ef4444] transition-colors px-2.5 py-1.5 rounded-lg hover:bg-[#ef4444]/5"
            title={user ? `Sign out (${user.email})` : "Sign out"}
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>

        <h2 className="font-outfit text-2xl font-bold text-[#0f172a] mb-1">Set up your business</h2>
        <p className="text-sm text-[#64748b] mb-6">Business profile for compliant invoicing.</p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label className="text-[#0f172a] font-medium text-sm flex items-center gap-1.5 mb-1.5">
              <Building2 className="h-3.5 w-3.5 text-[#64748b]" /> Legal Business Name *
            </Label>
            <Input value={form.business_name} onChange={(e) => update("business_name", e.target.value)}
              placeholder="ABC Corp Private Limited" data-testid="business-name-input" className="h-10 border-[#e2e8f0]" />
          </div>

          <div>
            <Label className="text-[#0f172a] font-medium text-sm mb-1.5 block">Registered Business Address</Label>
            <Input value={form.business_address} onChange={(e) => update("business_address", e.target.value)}
              placeholder="123 MG Road, Suite 45" data-testid="address-input" className="h-10 border-[#e2e8f0]" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#0f172a] font-medium text-sm mb-1.5 block">City</Label>
              <Input value={form.city} onChange={(e) => update("city", e.target.value)}
                placeholder="Mumbai" data-testid="city-input" className="h-10 border-[#e2e8f0]" />
            </div>
            <div>
              <Label className="text-[#0f172a] font-medium text-sm mb-1.5 block">PIN Code</Label>
              <Input value={form.pin} onChange={(e) => update("pin", e.target.value)} maxLength={6}
                placeholder="400001" data-testid="pin-input" className="h-10 border-[#e2e8f0] font-mono" />
            </div>
          </div>

          <div>
            <Label className="text-[#0f172a] font-medium text-sm flex items-center gap-1.5 mb-1.5">
              <MapPin className="h-3.5 w-3.5 text-[#64748b]" /> Home State *
            </Label>
            <Select value={form.state_code} onValueChange={(v) => update("state_code", v)}>
              <SelectTrigger data-testid="home-state-select" className="h-10 border-[#e2e8f0]">
                <SelectValue placeholder="Select your state">
                  {selectedState ? `${selectedState.code}-${selectedState.name}` : "Select your state"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {STATE_CODES.map((s) => (
                  <SelectItem key={s.code} value={s.code}>{s.code}-{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Business WhatsApp Number — mandatory for WhatsApp Business API link-up */}
          <div>
            <Label className="text-[#0f172a] font-medium text-sm flex items-center gap-1.5 mb-1.5">
              <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" /> Business WhatsApp Number *
            </Label>
            <Input
              type="tel"
              value={form.phone_number}
              onChange={(e) => update("phone_number", maskE164(e.target.value))}
              placeholder="+919812345678"
              inputMode="tel"
              autoComplete="tel"
              pattern="^\+[1-9][0-9]{6,14}$"
              data-testid="whatsapp-number-input"
              className={`h-10 border-[#e2e8f0] font-mono tracking-wide ${form.phone_number && !E164_REGEX.test(form.phone_number) ? "border-red-400 focus-visible:ring-red-200" : ""}`}
            />
            <p className="text-[11px] text-[#64748b] mt-1">
              Include country code with &apos;+&apos; (no spaces or dashes). Example: <span className="font-mono">+919812345678</span> for India.
            </p>
            {form.phone_number && !E164_REGEX.test(form.phone_number) && (
              <p className="text-[11px] text-red-500 mt-1" data-testid="whatsapp-number-error">
                Invalid format — must be E.164, e.g. +919812345678
              </p>
            )}
          </div>

          {/* Business Sector */}
          <div>
            <Label className="text-[#0f172a] font-medium text-sm mb-1.5 block">Business Sector</Label>
            <Select value={form.business_sector} onValueChange={(v) => update("business_sector", v)}>
              <SelectTrigger data-testid="business-sector-select" className="h-10 border-[#e2e8f0]">
                <SelectValue placeholder="Select sector">{form.business_sector || "Select sector"}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {BUSINESS_SECTORS.map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* GST Status */}
          <div className="bg-[#f8fafc] rounded-xl p-4 border border-[#e2e8f0]">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-[#4338ca]" />
              <Label className="text-sm font-medium text-[#0f172a]">GST Registration Status *</Label>
            </div>
            <Select value={form.gst_status} onValueChange={(v) => update("gst_status", v)}>
              <SelectTrigger data-testid="gst-status-select" className="h-10 border-[#e2e8f0]">
                <SelectValue>{GST_STATUSES.find(s => s.value === form.gst_status)?.label || "Select"}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {GST_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-[#64748b] mt-2">
              {form.gst_status === "registered_regular" && "Tax Invoices with full GST breakup"}
              {form.gst_status === "registered_composition" && "Bill of Supply — tax not collected from customers"}
              {form.gst_status === "unregistered" && "Commercial invoices without GST fields"}
            </p>
          </div>

          {/* GSTIN for registered */}
          {form.gst_status !== "unregistered" && (
            <div>
              <Label className="text-[#0f172a] font-medium text-sm mb-1.5 block">GSTIN (15 characters) *</Label>
              <Input value={form.gstin} onChange={(e) => update("gstin", e.target.value.toUpperCase())} maxLength={15}
                placeholder="29AABCT1234F1ZP" data-testid="gstin-input" className="h-10 border-[#e2e8f0] font-mono tracking-wide" />
              {form.gstin && form.gstin.length !== 15 && (
                <p className="text-xs text-red-500 mt-1">{form.gstin.length}/15 characters</p>
              )}
            </div>
          )}

          {/* Composition Category */}
          {form.gst_status === "registered_composition" && (
            <div>
              <Label className="text-[#0f172a] font-medium text-sm mb-1.5 block">Composition Category *</Label>
              <Select value={form.composition_category} onValueChange={(v) => update("composition_category", v)}>
                <SelectTrigger data-testid="composition-category-select" className="h-10 border-[#e2e8f0]">
                  <SelectValue placeholder="Select category">{COMPOSITION_CATEGORIES.find(c => c.value === form.composition_category)?.label || "Select category"}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {COMPOSITION_CATEGORIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <Button type="submit" disabled={saving} data-testid="onboarding-submit-btn"
            className="w-full h-11 bg-[#4338ca] hover:bg-[#3730a3] text-white rounded-xl font-medium shadow-lg shadow-[#4338ca]/20 active:scale-[0.98] mt-2">
            {saving ? "Setting up..." : "Get Started"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}
